import { BoxBase, StackBase, TypographyBase } from '@fmc-digital/ui';
import { NaturalDisasterAssistance, NaturalDisasterCards } from '@fmc-digital/servicing';
import React, { useCallback } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { withHeaderFooter } from 'components_v2/HOCs/withHeaderFooter/withHeaderFooter';
import { useNavigate } from 'react-router-dom';
import { clearLoanNDStatus } from '@store/actions/widgets.actions';
import { dispatch } from '@store';
import { DisasterAssistNavigationProps } from '@components_v2/data/header-data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EventLog, EventType, NaturalDisasterConstants, ResultCallBack, ToastType } from 'shared-ui-components';
import { ApiLogger } from 'fmc-digital-common';
import { logApiEvent, logUserEvent } from '@analytics/amplifyLogging';
import type { AxiosRequestConfig } from 'axios';
import { auth, config } from '../../../config';
import { Identifier } from '@types';
import { selectSelectedLoanNumber } from '@store/selectors/widgets.selectors';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { v4 } from 'uuid';
import { useSnackBarProps } from '@hooks/useSnackbarProps';

const useRequestForbearanceMutation = ({
    onComplete,
    logApiEvent,
    logUserEvent,
    type,
    accessToken
}: {
    accessToken: string;
    type: string;
} & ResultCallBack &
    EventLog) => {
    const correlationId = v4();
    return useMutation({
        mutationFn: (requestBody: any) => {
            const reqConfig: AxiosRequestConfig = {
                method: 'post',
                url: `${config.env('natDisasterAWSUrl')}/${requestBody.accountNumber}/natural-disaster/cit`,
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                    'x-correlation-id': correlationId,
                    'x-api-key': `${config.env('xApiKey')}`
                },
                data: JSON.stringify(requestBody)
            };
            return ApiLogger(reqConfig, logApiEvent);
        },
        onSuccess: async () => {
            logUserEvent({
                type,
                action: `${type}RequestForbearanceSuccess`,
                description: `Request Forbearance Success`
            });
            onComplete({
                message: NaturalDisasterConstants.SUCCESS_FORBEARANCE_MESSAGE,
                toastType: ToastType.SUCCESS
            });
        },
        onError: () => {
            logUserEvent({
                type,
                action: `${type}RequestForbearanceFailed`,
                description: `Request Forbearance Failed`
            });
            onComplete({
                message: NaturalDisasterConstants.FAILURE_FORBEARANCE_MESSAGE,
                toastType: ToastType.ERROR
            });
        }
    });
};

const DisasterAssistance = (): JSX.Element => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const currentLoan: Identifier = useSelector(selectSelectedLoanNumber);
    const queryClient = useQueryClient();
    const successSnackBar = useSnackBarProps({ severity: 'success' });
    const errorSnackBar = useSnackBarProps({ severity: 'error' });

    const requestBody = () => {
        if (!currentLoan) {
            return;
        }
        return { accountNumber: currentLoan?.identifier, taskMessage: 'Disaster', taskNumber: '784' };
    };
    const apiEvents = useCallback(
        (event: EventType) => {
            logApiEvent({
                apiName: event.apiName,
                action: event.action,
                correlationId: event.correlationId,
                timestamp: event.timestamp,
                loanNumber: currentLoan.identifier,
                description: event.description,
                type: 'Natural Disaster ACP widget'
            });
        },
        [currentLoan]
    );
    const userEvents = useCallback(
        (event: EventType) => {
            logUserEvent({
                type: 'Natural Disaster ACP widget',
                action: event.action,
                loanNumber: currentLoan.identifier,
                description: event.description
            });
        },
        [currentLoan]
    );
    const requestForbearance = useRequestForbearanceMutation({
        onComplete: async () => {
            dispatch(clearLoanNDStatus(currentLoan.identifier));
            await queryClient.invalidateQueries(['widgets']);
        },
        logApiEvent: apiEvents,
        logUserEvent: userEvents,
        type: 'Natural Disaster',
        accessToken: auth.getOkta().getAccessToken()
    });

    const backToHome = () => {
        navigate('../');
    };

    const handleClickEvent = (event: any) => {
        logUserEvent({
            type: 'Natural Disaster',
            action: event.action,
            loanNumber: currentLoan.identifier,
            description: event.action
        });
    };
    const pushSnackBarMessage = () => {
        logUserEvent({
            type: 'Natural Disaster',
            action: `DisasterRequestForbearanceClicked`,
            loanNumber: currentLoan.identifier,
            description: `Request Forbearance Button Clicked`
        });
        requestForbearance.mutate(requestBody(), {
            onSuccess: () => {
                enqueueSnackbar(NaturalDisasterConstants.SUCCESS_FORBEARANCE_MESSAGE, successSnackBar);
                navigate('../');
            },
            onError: () => {
                enqueueSnackbar(NaturalDisasterConstants.FAILURE_FORBEARANCE_MESSAGE, errorSnackBar);
            }
        });
    };
    return (
        <StackBase
            direction="column"
            alignItems="center"
            flexGrow={1}
            rowGap={{ xs: theme.spacing(2), md: theme.spacing(4) }}
            padding={{ xs: theme.spacing(2), md: theme.spacing(4) }}
        >
            <NaturalDisasterAssistance handleButtonClick={pushSnackBarMessage} backToHome={backToHome} />
            <BoxBase
                maxWidth={theme.spacing(160.25)}
                display="flex"
                flexDirection="column"
                rowGap={{ xs: theme.spacing(2), md: theme.spacing(4) }}
            >
                <NaturalDisasterCards logUserEvent={handleClickEvent} />
                {isMobile ? (
                    <StackBase direction="column">
                        <TypographyBase variant="paragraph" color="surface.dark" textAlign="center">
                            Have questions?
                        </TypographyBase>
                        <TypographyBase variant="paragraph" color="surface.dark" textAlign="center">
                            Call your loan advisor at 833-448-1927
                        </TypographyBase>
                    </StackBase>
                ) : (
                    <TypographyBase variant="paragraph" color="surface.dark" textAlign="center">
                        Have questions? Call your loan advisor at 833-448-1927
                    </TypographyBase>
                )}
            </BoxBase>
        </StackBase>
    );
};
export default withHeaderFooter(DisasterAssistance, { ...DisasterAssistNavigationProps, showFooterAds: true });
